import React, { useState, useEffect, useRef } from 'react';
import { orderService } from '../services/supabase';

import { realtimeService } from '../services/realtimeService';
import { soundService } from '../services/soundService';
import { notificationService } from '../services/notificationService';
import OrderNotificationModal from './OrderNotificationModal';
import NotificationSettings from './NotificationSettings';

const WaiterDashboard = ({ restaurant, waiter, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [refreshing, setRefreshing] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [currentNotificationOrder, setCurrentNotificationOrder] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [shiftStarted, setShiftStarted] = useState(false);
  const currentNotificationOrderRef = useRef(null);

  useEffect(() => {
    if (restaurant) {
      loadOrders();
      const interval = setInterval(loadOrders, 30000);
      return () => clearInterval(interval);
    }
  }, [restaurant, filter]);

  useEffect(() => {
    document.title = `Waiter Orders - ${restaurant?.name || 'Menuron'}`;
  }, [restaurant]);

  useEffect(() => {
    notificationService.requestPermission();
  }, []);

  // Subscribe to real-time order notifications
  useEffect(() => {
    if (!restaurant) return;

    console.log('Setting up realtime subscription for restaurant:', restaurant.id);

    const channel = realtimeService.subscribeToOrders(
      restaurant.id,
      async (newOrder) => {
        console.log('🔔 NEW ORDER RECEIVED:', newOrder);
        
        try {
          const { data: completeOrder, error } = await orderService.getOrderById(newOrder.id);
          
          if (error) {
            console.error('Error fetching order details:', error);
            return;
          }

          console.log('Complete order details:', completeOrder);
          
          const repeatEnabled = restaurant.notification_settings?.repeat_sound_enabled ?? true;
          const repeatInterval = restaurant.notification_settings?.repeat_interval_seconds ?? 20;
          
          if (repeatEnabled) {
            soundService.startRepeatingSound('bell-loud.mp3', repeatInterval, 1.0);
          } else {
            soundService.playSound('bell-loud.mp3', 1.0);
          }
          
          soundService.vibrate([300, 100, 300, 100, 500]);
          
          currentNotificationOrderRef.current = completeOrder;
          setCurrentNotificationOrder(completeOrder);
          setShowNotificationModal(true);
          
          loadOrders();
          
        } catch (error) {
          console.error('Error handling new order:', error);
        }
      },
      (payload) => {
        loadOrders();
        // If the confirmed order is the one currently alerting the waiter, stop pinging
        const confirmedId = payload?.orderId;
        if (confirmedId && currentNotificationOrderRef.current?.id === confirmedId) {
          soundService.stopRepeatingSound();
          setShowNotificationModal(false);
          setCurrentNotificationOrder(null);
          currentNotificationOrderRef.current = null;
        }
      }
    );

    return () => {
      console.log('Cleaning up realtime subscription');
      realtimeService.unsubscribe(channel);
      soundService.stopRepeatingSound();
    };
  }, [restaurant]);

  // Check if shift already started today
  useEffect(() => {
    const shiftDate = localStorage.getItem('shift_started_date');
    const today = new Date().toDateString();
    
    if (shiftDate === today) {
      setShiftStarted(true);
      console.log('✅ Shift already started today');
    } else {
      setShiftStarted(false);
      console.log('⏰ New shift - click Start Shift to enable notifications');
    }
  }, []);

  const loadOrders = async () => {
    try {
      const { data, error } = await orderService.getOrders(
        restaurant.id, 
        filter === 'all' ? null : filter
      );
      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      let result;
      if (newStatus === 'confirmed') {
        // Record waiter's name when confirming a QR order
        result = await orderService.waiterConfirmOrder(orderId, waiter?.name || 'Waiter');
      } else {
        result = await orderService.updateOrderStatus(orderId, newStatus);
      }

      if (result.error) throw result.error;

      setOrders(prev => prev.map(order =>
        order.id === orderId ? { ...order, status: newStatus } : order
      ));

      // Notify manager dashboard via HTTP broadcast (same reliable mechanism as Edge Function)
      if (newStatus === 'confirmed' && restaurant?.id) {
        realtimeService.broadcastOrderStatusUpdate(restaurant.id, orderId);
      }

      await loadOrders();

    } catch (error) {
      console.error('Error updating order status:', error);
      alert(`Failed to update order status: ${error.message}`);
    }
  };

  const refreshOrders = () => {
    setRefreshing(true);
    loadOrders();
  };

  const handleAcknowledgeOrder = () => {
    soundService.stopRepeatingSound();
    setShowNotificationModal(false);
    setCurrentNotificationOrder(null);
    currentNotificationOrderRef.current = null;
  };

  const handleCloseModal = () => {
    console.log('Modal closed, sound continues');
    setShowNotificationModal(false);
  };

  // Start shift function
  const startShift = () => {
    console.log('🚀 Starting shift...');
    
    const audio = new Audio('/sounds/bell-loud.mp3');
    audio.volume = 0.01;
    
    audio.play()
      .then(() => {
        console.log('✅ Audio context enabled - notifications ready!');
        setShiftStarted(true);
        
        const today = new Date().toDateString();
        localStorage.setItem('shift_started_date', today);
        
        console.log('🔔 You will now hear order notifications');
      })
      .catch(err => {
        console.error('❌ Failed to enable audio:', err);
        alert('Please tap the button again to enable notifications. Your browser requires user interaction to play sounds.');
      });
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-red-500',
      'confirmed': 'bg-green-500'
    };
    return colors[status] || colors['pending'];
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      'pending': 'confirmed'
    };
    return statusFlow[currentStatus];
  };

  const getStatusButton = (order) => {
    const nextStatus = getNextStatus(order.status);
    if (!nextStatus) return null;

    const buttonText = {
      'confirmed': 'Confirm Order'
    };

    return (
      <button
        onClick={() => updateOrderStatus(order.id, nextStatus)}
        style={{
          background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          padding: '8px 16px',
          fontSize: '0.9rem',
          fontWeight: '600',
          cursor: 'pointer',
          width: '100%',
          marginTop: '10px'
        }}
      >
        {buttonText[nextStatus]}
      </button>
    );
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const orderTime = new Date(timestamp);
    const diffMinutes = Math.floor((now - orderTime) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    return `${diffHours}h ${diffMinutes % 60}m ago`;
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '20px',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #FF4444',
            borderTop: '4px solid transparent',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }} />
          <h2 style={{ color: '#333', margin: '0' }}>Loading Orders...</h2>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: '0',
        zIndex: '100',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 68, 68, 0.1)',
        padding: '15px 0'  
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <img 
                src="https://i.ibb.co/hF3Q8YrX/menuron-high-resolution-logo.png" 
                alt="Menuron" 
                style={{ 
                  height: window.innerWidth <= 768 ? '70px' : '120px', 
                  marginRight: '15px', 
                  filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))' 
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ 
                  margin: '0 0 5px 0', 
                  fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.8rem', 
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Waiter Dashboard
                </h1>
                <p style={{ 
                  margin: '0', 
                  color: '#666', 
                  fontSize: window.innerWidth <= 768 ? '0.85rem' : '1rem',
                  wordBreak: 'break-word'
                }}>
                  {restaurant.name}
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                onClick={() => setShowSettings(true)}
                style={{
                  background: 'transparent',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  padding: '10px',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '44px',
                  height: '44px',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#FF6B00';
                  e.target.style.color = '#FF6B00';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.color = '#666';
                }}
                title="Notification Settings"
              >
                ⚙️
              </button>

              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  style={{
                    background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px',
                    fontSize: '1.2rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '44px',
                    height: '44px'
                  }}
                >
                  ☰
                </button>

                {showMobileMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: '0',
                    marginTop: '8px',
                    background: 'white',
                    borderRadius: '10px',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    minWidth: '160px',
                    zIndex: 1000
                  }}>
                    <button
                      onClick={() => {
                        refreshOrders();
                        setShowMobileMenu(false);
                      }}
                      disabled={refreshing}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'left',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        cursor: refreshing ? 'not-allowed' : 'pointer',
                        color: refreshing ? '#999' : '#333',
                        borderBottom: '1px solid #f0f0f0',
                        borderRadius: '10px 10px 0 0'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      {refreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <button
                      onClick={() => {
                        setShowMobileMenu(false);
                        onLogout();
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'left',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        color: '#dc3545',
                        borderRadius: '0 0 10px 10px'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {showMobileMenu && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'transparent',
              zIndex: 999
            }}
            onClick={() => setShowMobileMenu(false)}
          />
        )}
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 20px' }}>
        
        {/* START SHIFT BANNER */}
        {/* START SHIFT MODAL */}
        {!shiftStarted && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(8px)',
            animation: 'fadeIn 0.3s ease'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              position: 'relative',
              animation: 'slideUp 0.4s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}>
              
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 20px',
                background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3rem',
                boxShadow: '0 8px 25px rgba(255, 68, 68, 0.4)',
                animation: 'bounce 2s ease-in-out infinite'
              }}>
                🔔
              </div>

              <h2 style={{
                margin: '0 0 15px 0',
                fontSize: '2rem',
                fontWeight: '700',
                color: '#333'
              }}>
                Ready to Start Your Shift?
              </h2>

              <p style={{
                margin: '0 0 30px 0',
                fontSize: '1.1rem',
                color: '#666',
                lineHeight: '1.6'
              }}>
                Click the button below to start your shift and receive order notification.
              </p>

              <button
                onClick={startShift}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '15px',
                  padding: '18px 40px',
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 8px 25px rgba(255, 68, 68, 0.4)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 30px rgba(255, 68, 68, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 25px rgba(255, 68, 68, 0.4)';
                }}
              >
                
                Start Shift
              </button>

              <p style={{
                margin: '20px 0 0 0',
                fontSize: '0.85rem',
                color: '#999',
                fontStyle: 'italic'
              }}>
                You'll only need to do this once per day
              </p>
            </div>

            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              
              @keyframes slideUp {
                from { 
                  transform: translateY(50px);
                  opacity: 0;
                }
                to { 
                  transform: translateY(0);
                  opacity: 1;
                }
              }
              
              @keyframes bounce {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-10px); }
              }
            `}</style>
          </div>
        )}
        
        {/* Filter Tabs */}
        <div style={{ 
          background: 'white',
          borderRadius: '15px',
          padding: '8px',
          marginBottom: '30px',
          display: 'flex',
          gap: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          {['pending', 'confirmed', 'all'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                textTransform: 'capitalize',
                background: filter === status 
                  ? 'linear-gradient(135deg, #FF4444, #FF6B00)' 
                  : 'transparent',
                color: filter === status ? 'white' : '#666',
                transition: 'all 0.3s ease'
              }}
            >
              {status}
            </button>
          ))}
        </div>

        {/* Orders Grid */}
        {orders.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 40px',
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📋</div>
            <h3 style={{ 
              color: '#333', 
              margin: '0 0 10px 0',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              No {filter === 'all' ? '' : filter} orders
            </h3>
            <p style={{ color: '#666', margin: '0', fontSize: '1.1rem' }}>
              {filter === 'pending' 
                ? "No new orders at the moment"
                : `No ${filter} orders found`
              }
            </p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '20px'
          }}>
            {orders.map(order => (
              <div key={order.id} style={{
                background: 'white',
                borderRadius: '15px',
                padding: '20px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255, 68, 68, 0.1)',
                borderLeft: `4px solid ${order.status === 'pending' ? '#dc2626' : '#16a34a'}`
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: '15px'
                }}>
                  <div>
                    <h3 style={{ 
                      margin: '0 0 5px 0', 
                      fontSize: '1.3rem', 
                      fontWeight: '700',
                      color: '#333'
                    }}>
                      {order.restaurant_tables?.custom_name || `Table ${order.restaurant_tables?.table_number}`}
                    </h3>
                    <p style={{ 
                      margin: '0 0 5px 0', 
                      color: '#666', 
                      fontSize: '0.9rem'
                    }}>
                      #{order.order_number}
                    </p>
                    <p style={{ 
                      margin: '0', 
                      color: '#999', 
                      fontSize: '0.85rem'
                    }}>
                      {formatTime(order.created_at)} • {getTimeAgo(order.created_at)}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: '700',
                      background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      marginBottom: '5px'
                    }}>
                      {restaurant?.default_currency || '₹'}{order.total_amount}
                    </div>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      background: order.status === 'pending' ? '#fee2e2' : '#dcfce7',
                      color: order.status === 'pending' ? '#dc2626' : '#16a34a'
                    }}>
                      {order.status}
                    </span>
                  </div>
                </div>

                <div style={{ 
                  background: '#f8f9fa',
                  borderRadius: '10px',
                  padding: '15px',
                  marginBottom: '15px'
                }}>
                  <h4 style={{ 
                    margin: '0 0 10px 0', 
                    fontSize: '1rem', 
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    Order Items:
                  </h4>
                  {order.order_items?.map(item => (
                    <div key={item.id} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px',
                      paddingBottom: '8px',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      <div>
                        <span style={{ fontWeight: '600', color: '#333' }}>
                          {item.quantity}x {item.menu_items?.name}
                        </span>
                      </div>
                      <span style={{ 
                        fontWeight: '600',
                        color: '#FF6B00'
                      }}>
                        {restaurant?.default_currency || '₹'}{item.total_price}
                      </span>
                    </div>
                  ))}
                </div>

                {order.notes && (
                  <div style={{ 
                    background: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '8px',
                    padding: '10px',
                    marginBottom: '15px'
                  }}>
                    <strong style={{ color: '#856404' }}>Notes:</strong>
                    <p style={{ margin: '5px 0 0 0', color: '#856404' }}>{order.notes}</p>
                  </div>
                )}

                {getStatusButton(order)}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {showNotificationModal && currentNotificationOrder && (
        <OrderNotificationModal
          order={currentNotificationOrder}
          restaurant={restaurant} 
          onAcknowledge={handleAcknowledgeOrder}
          onClose={handleCloseModal}
        />
      )}

      {showSettings && (
        <NotificationSettings onClose={() => setShowSettings(false)} />
      )}

    </div>
  );
};

export default WaiterDashboard;